import React, {useMemo} from 'react';
import {ReactFlow, ReactFlowProvider, Background, MarkerType} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../../../styles/components/data-lineage/lineage-editor.scss';
import AppNode from '../../data-lineage/components/AppNode';
import GroupNode from '../../data-lineage/components/GroupNode';
import {LineageContext} from '../../data-lineage/components/LineageContext';

const nodeTypes = {appNode: AppNode, groupNode: GroupNode};

const NOOP_ACTIONS = {
  selectElement() {},
  addElement() {},
  updateElement() {},
  removeElement() {},
  openNodeDrawer() {},
  toggleGroupCollapse() {},
  updateNode() {},
  fetchDdColumns() {},
};

const buildNodes = (characteristics) => [
  {
    id: 'preview-group',
    type: 'groupNode',
    position: {x: 20, y: 150},
    style: {width: 200, height: 150},
    data: {label: 'Ingestion', tables: [], dataElements: []},
  },
  {
    id: 'preview-svc',
    type: 'appNode',
    parentId: 'preview-group',
    extent: 'parent',
    position: {x: 20, y: 40},
    data: {
      label: 'Auth',
      appType: 'service',
      details: {},
      dataElements: [],
      tables: [],
      characteristicId: null,
      isSource: false,
      isDestination: false,
    },
  },
  {
    id: 'preview-file',
    type: 'appNode',
    parentId: 'preview-group',
    extent: 'parent',
    position: {x: 20, y: 95},
    data: {
      label: 'Export',
      appType: 'file',
      details: {},
      dataElements: [],
      tables: [],
      characteristicId: null,
      isSource: false,
      isDestination: false,
    },
  },
  {
    id: 'preview-db',
    type: 'appNode',
    position: {x: 270, y: 20},
    data: {
      label: 'Orders DB',
      appType: 'database',
      details: {},
      dataElements: [],
      tables: [],
      characteristicId: (characteristics[0] && characteristics[0].id) || null,
      isSource: false,
      isDestination: false,
    },
  },
  {
    id: 'preview-app',
    type: 'appNode',
    position: {x: 520, y: 20},
    data: {
      label: 'Billing',
      appType: 'application',
      details: {},
      dataElements: [],
      tables: [],
      characteristicId: null,
      isSource: false,
      isDestination: true,
    },
  },
  {
    id: 'preview-api',
    type: 'appNode',
    position: {x: 520, y: 150},
    data: {
      label: 'Partner API',
      appType: 'api',
      details: {isExternal: true},
      dataElements: [],
      tables: [],
      characteristicId: null,
      isSource: true,
      isDestination: false,
    },
  },
];

const buildEdges = (theme) => [
  {
    id: 'e1',
    source: 'preview-db',
    target: 'preview-app',
    markerEnd: {type: MarkerType.ArrowClosed, color: theme.edge.color},
    style: {stroke: theme.edge.color, strokeWidth: theme.edge.width},
  },
  {
    id: 'e2',
    source: 'preview-api',
    target: 'preview-app',
    animated: true,
    markerEnd: {type: MarkerType.ArrowClosed, color: theme.edge.highlightColor},
    style: {stroke: theme.edge.highlightColor, strokeWidth: theme.edge.highlightWidth},
  },
];

const SampleLineageCanvas = ({theme, characteristics}) => {
  const safeChars = characteristics || [];

  const nodes = useMemo(() => buildNodes(safeChars), [safeChars]);
  const edges = useMemo(() => buildEdges(theme), [theme]);

  const contextValue = useMemo(() => ({
    lineageViewType: 'system',
    viewMode: 'view',
    theme,
    actions: NOOP_ACTIONS,
    characteristics: safeChars,
    ddColumns: {},
  }), [theme, safeChars]);

  return (
    <div style={{width: '100%', height: '100%'}} data-testid="lc-sample-canvas">
      <ReactFlowProvider>
        <LineageContext.Provider value={contextValue}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag={false}
            zoomOnScroll={false}
            fitView
            proOptions={{hideAttribution: true}}
          >
            <Background />
          </ReactFlow>
        </LineageContext.Provider>
      </ReactFlowProvider>
    </div>
  );
};

export default SampleLineageCanvas;
